
import React, { useState, useEffect } from 'react';
import { GlassCard, Button } from '../components/shared';
import { Language, DailyTask, SubjectType, Theme } from '../types';
import { translations } from '../i18n';
import { Minus, Plus, Zap, Play, Pause, X, Trophy, Clock, CheckCircle2, Trash2, ListTodo } from 'lucide-react';

interface FocusStationProps {
  onComplete: (energy: number) => void;
  onCancel: () => void;
  lang: Language;
  theme: Theme;
  tasks: DailyTask[];
  onAddTask: (subject: SubjectType) => void;
  onRemoveTask: (id: string) => void;
  onToggleTask: (id: string) => void;
}

export const FocusStation: React.FC<FocusStationProps> = ({ 
  onComplete, onCancel, lang, theme, tasks, onAddTask, onRemoveTask, onToggleTask 
}) => {
  const [status, setStatus] = useState<'idle' | 'running' | 'paused' | 'completed'>('idle');
  const [activeTab, setActiveTab] = useState<'timer' | 'tasks'>('tasks');
  const [duration, setDuration] = useState(25);
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const t = translations[lang];

  const isDark = theme === 'dark';

  useEffect(() => {
    let interval: any = null;
    if (status === 'running' && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && status === 'running') {
      setStatus('completed');
    }
    return () => clearInterval(interval);
  }, [status, timeLeft]);

  const handleStart = () => {
    setTimeLeft(duration * 60);
    setStatus('running');
    setActiveTab('timer');
  };

  const handleAdjust = (delta: number) => {
    setDuration(prev => Math.min(120, Math.max(5, prev + delta)));
  };

  const handleFinish = () => {
    onComplete(duration);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const totalSeconds = duration * 60;
  const progress = ((totalSeconds - timeLeft) / totalSeconds) * 100;

  // Theme-aware styles
  const bgOverlay = isDark ? 'bg-slate-950/90' : 'bg-slate-200/60';
  const cardClass = isDark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900';
  const subText = isDark ? 'text-slate-400' : 'text-slate-500';
  const inputBg = isDark ? 'bg-black/30' : 'bg-slate-100';

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md animate-in fade-in duration-300 ${bgOverlay}`}>
      <GlassCard 
        variant={isDark ? 'dark' : 'light'} 
        className={`w-full max-w-md mx-4 p-0 border shadow-2xl relative overflow-hidden flex flex-col max-h-[80vh] ${isDark ? 'border-white/10' : 'border-slate-200'}`}
      >
        
        {/* Background Decorative */}
        <div className="absolute top-[-50px] right-[-50px] w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-[-50px] left-[-50px] w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* Header */}
        <div className="p-6 pb-2 text-center relative z-10 shrink-0">
           <h2 className={`text-2xl font-orbitron font-black flex items-center justify-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
             <Zap className="fill-yellow-400 text-yellow-400" size={24}/>
             {t.energyStation}
           </h2>
           <p className={`${subText} text-xs mt-1 font-bold`}>{t.energyTasks}</p>
        </div>

        {/* Tabs */}
        {status === 'idle' && (
           <div className="flex gap-2 px-6 mb-4 shrink-0">
              <button 
                onClick={() => setActiveTab('tasks')}
                className={`flex-1 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'tasks' ? 'bg-indigo-600 text-white shadow-lg' : isDark ? 'bg-slate-800 text-slate-500 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                 {t.missionBoard}
              </button>
              <button 
                onClick={() => setActiveTab('timer')}
                className={`flex-1 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'timer' ? 'bg-indigo-600 text-white shadow-lg' : isDark ? 'bg-slate-800 text-slate-500 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                 {t.focus}
              </button>
           </div>
        )}

        {/* CONTENT AREA */}
        <div className="flex-1 overflow-y-auto p-6 pt-0">
            
            {status === 'idle' && activeTab === 'tasks' && (
                <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                    <div className="space-y-3">
                        {tasks.length === 0 ? (
                             <div className={`text-center py-10 border-2 border-dashed rounded-2xl ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <ListTodo className={`mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} size={32} />
                                <p className={`text-sm font-bold ${subText}`}>{t.addHomework}</p>
                             </div>
                        ) : (
                            tasks.map(task => (
                                <div key={task.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all shadow-sm ${task.completed ? 'bg-emerald-500/10 border-emerald-500/30' : isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200 hover:border-indigo-300'}`}>
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${task.completed ? 'bg-emerald-500 text-white' : isDark ? 'bg-slate-700 text-slate-300 border-white/5' : 'bg-white text-slate-400 border-slate-200'}`}>
                                            {task.completed ? <CheckCircle2 size={18}/> : task.subject[0].toUpperCase()}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className={`text-sm font-bold truncate ${task.completed ? 'text-emerald-600 line-through' : isDark ? 'text-white' : 'text-slate-800'}`}>{task.desc}</span>
                                            <span className={`text-[10px] font-bold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{task.subject}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {!task.completed && (
                                            <button 
                                                onClick={() => setActiveTab('timer')} 
                                                className="p-2 bg-yellow-500 rounded-lg text-slate-900 hover:bg-yellow-400 transition-colors shadow-lg shadow-yellow-500/20"
                                            >
                                                <Zap size={16} className="fill-current"/>
                                            </button>
                                        )}
                                        <button onClick={() => onRemoveTask(task.id)} className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-slate-700/50 text-slate-400 hover:text-red-400' : 'bg-slate-200 text-slate-500 hover:text-red-500'}`}>
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="pt-2">
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{t.addTask}</p>
                        <div className="grid grid-cols-3 gap-2">
                            {(['math', 'chinese', 'english'] as SubjectType[]).map(s => (
                                <button 
                                    key={s}
                                    onClick={() => onAddTask(s)}
                                    className={`py-3 rounded-xl text-xs font-bold transition-all shadow-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                >
                                    {t.subjects[s]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {status === 'idle' && activeTab === 'timer' && (
            <div className="animate-in slide-in-from-left-4 duration-300">
                <div className={`flex items-center justify-between rounded-[2rem] p-6 border mb-6 ${inputBg} ${isDark ? 'border-white/5' : 'border-slate-200 shadow-inner'}`}>
                    <button 
                        onClick={() => handleAdjust(-5)}
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 shadow-lg border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Minus size={24}/>
                    </button>
                    
                    <div className="text-center">
                        <div className={`text-6xl font-black font-orbitron tabular-nums drop-shadow-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>{duration}</div>
                        <div className={`text-xs font-bold uppercase tracking-widest mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{t.minutes}</div>
                    </div>

                    <button 
                        onClick={() => handleAdjust(5)}
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 shadow-lg border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Plus size={24}/>
                    </button>
                </div>

                <div className="flex items-center justify-center gap-2 text-yellow-600 bg-yellow-500/10 py-3 rounded-xl border border-yellow-500/20 mb-8 font-bold">
                  <Trophy size={16} className="fill-yellow-500/20"/>
                  <span className="text-sm uppercase tracking-wider">{t.estReward}: +{duration} Energy</span>
                </div>

                <Button onClick={handleStart} className="w-full py-5 text-xl bg-yellow-500 hover:bg-yellow-400 text-slate-900 shadow-xl shadow-yellow-500/20 border-0">
                    {t.startFocus}
                </Button>
            </div>
            )}

            {(status === 'running' || status === 'paused') && (
            <div className="flex flex-col items-center animate-in zoom-in-95 duration-300 py-4">
                <div className="relative w-64 h-64 flex items-center justify-center mb-10">
                    <svg className="absolute w-full h-full transform -rotate-90 drop-shadow-2xl">
                      <circle cx="128" cy="128" r="110" stroke={isDark ? "#1e293b" : "#e2e8f0"} strokeWidth="16" fill="transparent" />
                      <circle cx="128" cy="128" r="110" stroke="#eab308" strokeWidth="16" fill="transparent" 
                              strokeDasharray={2 * Math.PI * 110} 
                              strokeDashoffset={2 * Math.PI * 110 * (1 - progress/100)} 
                              strokeLinecap="round" className="transition-all duration-1000 ease-linear" />
                    </svg>
                    
                    <div className="text-center z-10">
                      <div className={`text-6xl font-black font-orbitron tabular-nums drop-shadow-md ${status === 'paused' ? 'opacity-50' : ''} ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {formatTime(timeLeft)}
                      </div>
                      {status === 'paused' && <div className="text-yellow-600 font-black uppercase tracking-widest text-sm mt-2">{t.pause}</div>}
                    </div>
                </div>

                <div className="flex gap-4 w-full">
                    <Button 
                      onClick={() => setStatus(status === 'running' ? 'paused' : 'running')}
                      className={`flex-1 border-0 shadow-lg ${isDark ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-700'}`}
                    >
                      {status === 'running' ? <Pause size={24}/> : <Play size={24}/>}
                    </Button>
                    <Button 
                      onClick={onCancel}
                      className="flex-1 bg-red-500/10 text-red-500 hover:bg-red-500/20 border border-red-500/20"
                    >
                      <X size={24}/>
                    </Button>
                </div>
            </div>
            )}

            {status === 'completed' && (
            <div className="text-center animate-in zoom-in-95 duration-300 py-6">
                <div className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-yellow-500/40 animate-bounce">
                    <Trophy size={48} className="text-slate-900 fill-current" />
                </div>
                <h3 className={`text-2xl font-black mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.goodJob}</h3>
                <p className={`${subText} mb-8`}>{t.energyRecharged}</p>
                
                <div className="text-5xl font-black text-yellow-600 mb-8 drop-shadow-sm">+{duration} <Zap size={32} className="inline fill-current"/></div>

                <Button onClick={handleFinish} className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 text-slate-900 shadow-xl border-0">
                    {t.claimReward}
                </Button>
            </div>
            )}

        </div>
        
        {status === 'idle' && (
            <button onClick={onCancel} className={`p-4 w-full text-sm font-bold transition-colors border-t ${isDark ? 'text-slate-500 hover:text-white border-white/5 bg-black/20' : 'text-slate-400 hover:text-slate-900 border-slate-100 bg-slate-50'}`}>
                {t.cancel}
            </button>
        )}

      </GlassCard>
    </div>
  );
};
